import fetchProduct from "../../helpers/useProduct/fetchProduct";
import formatDatalayerEcommerce from "../../helpers/formatDatalayerEcommerce";

class AddToCart extends HTMLElement {
  constructor() {
    super();

    if (this.hasAttribute("tracking-only")) {
      this.addEventListener("click", () => {
        const { data, productId, isQuickView } = this.getData(
          this.closest("form"),
        );

        const variantId = `gid://shopify/ProductVariant/${data.id}`;

        this.sendTracking({
          productId,
          variantId,
          isQuickView,
          quantity: data.quantity,
          isBuyNowButton: true,
        });
      });
    } else {
      this.button = this.closest("button");
      this.form = this.button?.form;

      if (!this.form) return;

      this.onSubmit = (e) => {
        e.preventDefault();
        this.setLoading(true);

        if (this._secondary) return;

        const { data, productId, isQuickView } = this.getData(this.form);
        const variantId = `gid://shopify/ProductVariant/${data.id}`;

        this.sendTracking({
          productId,
          variantId,
          isQuickView,
          quantity: data.quantity,
          isBuyNowButton: false,
        });

        data.quantity = parseInt(data.quantity);

        window.addToCart(data, {
          onError: (response) => {
            document.dispatchEvent(new CustomEvent("add-to-cart:complete"));
            this.showError(
              response?.message ||
                window.localeStrings.product.add_to_cart_error,
            );
          },
          onAfterAdd: () => {
            document.dispatchEvent(new CustomEvent("markup-modal:close"));
            document.dispatchEvent(new CustomEvent("mini-cart:open"));
            document.dispatchEvent(new CustomEvent("add-to-cart:complete"));
          },
        });
      };

      this.onAfterAdd = () => this.setLoading(false);

      document.addEventListener("add-to-cart:complete", this.onAfterAdd);

      this.form.addEventListener("submit", this.onSubmit);
    }
  }

  disconnectedCallback() {
    document.removeEventListener("add-to-cart:complete", this.onAfterAdd);
    this.form?.removeEventListener("submit", this.onSubmit);
  }

  showError(message) {
    const error = document.createElement("div");
    error.classList.add("add-to-cart-error");
    error.innerHTML = message;

    this.form.append(error);

    setTimeout(() => {
      error.remove();
    }, 3000);
  }

  getData(form) {
    const formData = new FormData(form);
    const allowedKeys = ["id", "quantity"];
    const data = {};
    let productId = null;
    let isQuickView = false;

    // Add each name/value pair to the object
    for (const [name, value] of formData) {
      const property = /properties\[(?<propertyKey>.+)\]/gm.exec(name);

      if (name === "_product_gid") {
        productId = value;
      }

      if (name === "properties[_is_quick_view]" && value === "true") {
        isQuickView = true;
      }

      if (property || allowedKeys.includes(name)) {
        if (!property) {
          data[name] = value;
        } else {
          if (!("properties" in data)) {
            data.properties = {};
          }
          data.properties[property.groups.propertyKey] = value;
        }
      }
    }

    if (!("quantity" in data)) {
      data.quantity = 1;
    }

    return { data, productId, isQuickView };
  }

  sendTracking({
    productId,
    variantId,
    isQuickView,
    quantity,
    isBuyNowButton,
  }) {
    if (productId) {
      fetchProduct(productId).then((product) => {
        const variant = product.variants.find(
          (variant) => variant.id === variantId,
        );

        const customProperties = {
          is_quick_view: isQuickView,
          is_buy_now_button: isBuyNowButton,
          is_product_set: false,
          quantity: `${quantity}`,
        };

        const ecommerce = formatDatalayerEcommerce({
          product: product,
          variant,
          key: "add",
          customProperties,
        });

        window.ElevarDataLayer?.push({
          event: "dl_add_to_cart",
          ...ecommerce,
        });
      });
    }
  }

  get _secondary() {
    return this.hasAttribute("secondary");
  }

  // add-to-cart:complete is a document-level event, so every button on the page
  // hears it; only the one that stored its label was the one that submitted.
  setLoading(loading) {
    if (loading) {
      this.text = this.innerHTML;
      this.innerHTML = window.localeStrings.product.adding_to_cart;
    } else if (this.text != null) {
      this.innerHTML = this.text;
      this.text = null;
    }
  }
}

if (!customElements.get("add-to-cart")) {
  customElements.define("add-to-cart", AddToCart);
}
